/**
 * **A10 · The chair's area one-pager** (Stream A, Paper.dc.html), laid out by the `area_page`
 * engine: the three things, the area's budget lines (budget · spent · left, from Budget), the
 * roster by day (filled of seats, from Volunteers), the chairs and the finance director from Roles,
 * and who to call from the radio plan. Read through the chair's own doors, so a chair prints their
 * own area; a read the chair may not make prints NO DATA. The chair app is the live copy.
 */
import type { ReactNode } from 'react';
import { FestivalClock, areaPage, type RadioPlan } from '@cider/domain/engines';
import { formatTime } from '@cider/ui';
import { fiscalYearToday, useLoad } from '../views/money/shared.js';
import { usePrinted } from './printed.js';
import { DrawnPages } from './drawn-doc.js';
import { holder } from './aod-package.js';

interface Lines {
  lines: {
    accountId: string;
    accountNumber: string;
    accountName: string;
    lineCents: number;
    spentCents: number;
    remainingCents: number | null;
  }[];
}
interface Shifts {
  shifts: {
    id: string;
    areaName?: string;
    day: string;
    startsAt: string;
    endsAt: string;
    title: string;
    capacity: number;
    filled: number;
    published: boolean;
  }[];
}
interface Role {
  name: string;
  role: string;
  areaId: string | null;
  state: string;
  named?: boolean;
}

/** `12:00 PM` → `12 PM`: the roster's hours as the drawing writes them. */
const hour = (iso: string): string => formatTime(iso).replace(':00', '');

export function AreaPage({ year, areaId }: { year: string; areaId: string }): ReactNode {
  const fy = fiscalYearToday();
  const money = useLoad<Lines>(`/money/budget/${String(fy)}/mine?areaId=${areaId}`);
  const vol = useLoad<Shifts>(`/volunteers/chair/shifts?year=${year}&areaId=${areaId}`);
  const roles = useLoad<Role[]>(`/admin/roles/year?year=${year}`);
  const plan = useLoad<RadioPlan>(`/ops/radio-plan?year=${year}`);
  const loads = [money, vol, roles, plan];
  const ready = !!vol.data && loads.every((l) => l.data !== null || l.error !== null);
  usePrinted('area-page', ready, { ref: areaId, year: Number(year) });
  if (vol.error) return <p role="alert">{vol.error}</p>;
  if (!ready || !vol.data) return <p>Reading the area…</p>;
  const live = (roles.data ?? []).filter((r) => r.state === 'active' || r.state === 'granted');
  const chairs = live
    .filter((r) => r.areaId === areaId && r.role === 'area_chair')
    .map((r) => r.name);
  const p = plan.data;
  const page = areaPage({
    year: Number(year),
    area: vol.data.shifts.find((s) => s.areaName)?.areaName ?? null,
    chairs: [...new Set(chairs)],
    lines: money.data
      ? money.data.lines.map((l) => ({
          line: `${l.accountNumber} · ${l.accountName}`,
          budgetCents: l.lineCents,
          spentCents: l.spentCents,
          leftCents: l.remainingCents,
        }))
      : null,
    shifts: vol.data.shifts
      .filter((s) => s.published)
      .map((s) => ({
        day: FestivalClock.operatingDay(new Date(s.startsAt)),
        time: `${hour(s.startsAt)} – ${hour(s.endsAt)}`,
        who: s.title,
        filled: s.filled,
        seats: s.capacity,
      })),
    plan: p
      ? {
          chairsChannel: p.channels.find((c) => /chairs/i.test(c.use))?.channel ?? null,
          securityChannel: p.securityChannel,
          dispatchPhone: p.dispatchPhone,
        }
      : null,
    financeDirector: holder(roles.data, 'finance_director'),
  });
  return <DrawnPages pages={[page]} stamp="time" />;
}
